import React, { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  CButton, CCard, CCardBody, CCol, CForm, CFormInput,
  CFormLabel, CAlert, CSpinner,
} from '@coreui/react'
import { registerInit } from '../api/client'

// [RGM] SiteKey de Cloudflare Turnstile (diseño nuevo aprobado). Si está
// vacío (dev) el widget no se renderiza y el backend permite el paso.
const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY ?? ''

// [RGM] Flag a nivel de módulo: el script del widget se carga una sola vez
// por sesión de página; evita re-render tras remounts del componente.
let turnstileScriptLoaded = false

/**
 * Registro paso 1 — detalle visual del demo CoreUI (mismo card del login).
 * [RGM] Diseño nuevo aprobado por Rafael (el LowCode no tiene verificación
 * por enlace): email + Turnstile anti-bot → el backend envía un email de
 * VERIFICACIÓN vía Resend; el frontend NO navega directo, muestra
 * "Revisa tu correo para continuar".
 */
const RegisterEmailPage: React.FC = () => {
  const [email, setEmail] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [turnstileToken, setTurnstileToken] = useState('')
  const [emailSent, setEmailSent] = useState(false)
  const turnstileRef = useRef<HTMLDivElement>(null)

  // [RGM] Carga del widget Turnstile — diseño NUEVO aprobado por Rafael
  // (NO existe en la KB GX; el registro web GX no usa captcha): script bajo
  // demanda con ?render=explicit + render explícito del widget.
  useEffect(() => {
    if (!TURNSTILE_SITE_KEY || !turnstileRef.current) return

    const renderWidget = () => {
      const w = window as unknown as { turnstile?: { render: (el: HTMLElement, opts: { sitekey: string; callback: (t: string) => void; theme?: 'light' | 'dark' | 'auto' }) => void } }
      const container = turnstileRef.current
      if (!w.turnstile || !container || container.dataset.turnstileRendered) return
      w.turnstile.render(container, {
        sitekey: TURNSTILE_SITE_KEY,
        callback: (token: string) => setTurnstileToken(token),
        // Tema CLARO (blanco) — pedido Rafael 2026-08-26 (antes oscuro)
        theme: 'light',
      })
      container.dataset.turnstileRendered = '1'
    }

    if (turnstileScriptLoaded || window.turnstile) {
      turnstileScriptLoaded = true
      renderWidget()
      return
    }

    const script = document.createElement('script')
    // render=explicit: SIN ese parámetro Cloudflare auto-renderiza y
    // window.turnstile.render NO queda disponible → el widget no aparece.
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
    script.async = true
    script.onload = () => {
      turnstileScriptLoaded = true
      renderWidget()
    }
    document.body.appendChild(script)
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      // [RGM] Diseño NUEVO aprobado por Rafael (el registro web GX no
      // existe en la KB — no hay verificación por enlace en GX): registerInit
      // valida el email + Turnstile y dispara el envío de verificación.
      const token = TURNSTILE_SITE_KEY ? turnstileToken : undefined
      await registerInit(email.trim(), token)
      // Flujo ORIGINAL (restaurado por Rafael 2026-08-26): tras verificar el
      // correo se muestra el mensaje de envío — el registro se completa con
      // el link del email ("hacé clic en el enlace... para completar").
      setEmailSent(true)
    } catch (err: any) {
      setError(err.message ?? 'No se pudo verificar el correo')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="bg-body-tertiary min-vh-100 d-flex align-items-center justify-content-center p-3">
      <div style={{ width: '100%', maxWidth: 460 }}>
        <div className="d-flex flex-column gap-4">
          <div className="text-center">
            <img src="/logo_siprinia.png" alt="SiprinIA" height={72} />
          </div>
              <CCard className="p-4">
                <CCardBody className="d-flex flex-column gap-4">
                  <h2 className="h5 text-center mb-0">Crear cuenta</h2>
                  <p className="text-center text-body-secondary mb-0">
                    Ingresa tu correo electrónico para comenzar el registro
                  </p>
                  {error && <CAlert color="danger" className="mb-0">{error}</CAlert>}
                  {emailSent && (
                    <CAlert color="success" className="mb-0">
                      Revisa tu correo para continuar
                    </CAlert>
                  )}
                  {!emailSent ? (
                    <CForm className="row gy-3" onSubmit={handleSubmit}>
                      <CCol xs={12}>
                        <CFormLabel htmlFor="reg-email">Correo electrónico</CFormLabel>
                        <CFormInput
                          id="reg-email"
                          type="email"
                          placeholder="correo@empresa.com"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          autoComplete="email"
                          required
                        />
                      </CCol>
                      {TURNSTILE_SITE_KEY && (
                        <CCol xs={12}>
                          {/* [RGM] Widget Turnstile (Cloudflare) — solo si hay siteKey */}
                          <div ref={turnstileRef} className="d-flex justify-content-center" />
                        </CCol>
                      )}
                      <CCol xs={12}>
                        <CButton color="primary" type="submit" disabled={loading} className="w-100">
                          {loading ? <CSpinner size="sm" /> : 'Verificar correo'}
                        </CButton>
                      </CCol>
                  </CForm>
                  ) : (
                    <div className="text-center text-body-secondary">
                      Revisá tu casilla de correo y hacé clic en el enlace que te enviamos para
                      completar el registro.
                    </div>
                  )}
                </CCardBody>
              </CCard>
          <div className="text-center text-body-secondary">
            ¿Ya tienes una cuenta? <Link to="/login">Iniciar sesión</Link>
          </div>
        </div>
      </div>
    </div>
  )
}

export default RegisterEmailPage
